import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";
import MobileHeader from "../../components/MobileHeader";
import GroupScoreCard from "../../components/GroupScoreCard";
import { useI18n } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Users, CheckCircle2, Clock, XCircle, AlertTriangle,
    Wallet as WalletIcon, ShoppingBag, Crown,
} from "lucide-react";

const STATUS_META = {
    pending_co_signers: { label: "Awaiting co-signers", color: "amber", icon: Clock },
    active:             { label: "Active",              color: "blue",  icon: ShoppingBag },
    repaid:             { label: "Repaid",              color: "green", icon: CheckCircle2 },
    cancelled:          { label: "Cancelled",           color: "slate", icon: XCircle },
};

const SHARE_STATUS_META = {
    pending_acceptance: { label: "Pending acceptance", color: "amber" },
    active:             { label: "Active",             color: "blue" },
    repaid:             { label: "Repaid",             color: "green" },
    overdue:            { label: "Overdue",            color: "rose" },
    cancelled:          { label: "Cancelled",          color: "slate" },
};

export default function SplitPayDetail() {
    const { loanId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const { t } = useI18n();
    const isBusiness = user?.role === "business";
    const baseRoute = isBusiness ? "/business" : "/consumer";
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [repayOpen, setRepayOpen] = useState(false);
    const [repayAmount, setRepayAmount] = useState("");

    const load = async () => {
        try {
            const r = await api.get(`/splitpay/${loanId}`);
            setData(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load");
        }
    };

    useEffect(() => {
        load();
    }, [loanId]);

    if (!data) {
        return (
            <div data-testid="splitpay-detail-page">
                <MobileHeader title="SplitPay™" backTo={`${baseRoute}/splitpay`} variant="purple" />
                <div className="p-12 text-center text-slate-400">Loading…</div>
            </div>
        );
    }

    const { loan, members, my_member } = data;
    const isSupplierBulk = loan.loan_type === "supplier_bulk";
    const isSupplier = my_member.role === "supplier";
    const meta = STATUS_META[loan.status] || STATUS_META.active;
    const Icon = meta.icon;
    const amIInitiator = my_member.role === "initiator" || my_member.role === "supplier";
    const canAccept = !my_member.accepted && loan.status === "pending_co_signers";
    const canRepay = loan.status === "active" && my_member.share_total > 0 && my_member.share_repaid < my_member.share_total;
    const myOutstanding = Math.max(0, my_member.share_total - (my_member.share_repaid || 0));

    const accept = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/splitpay/${loanId}/accept`);
            toast.success(r.data.activated ? "Loan activated & funds disbursed!" : "Accepted. Waiting for other co-signers.");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const decline = async () => {
        setBusy(true);
        try {
            await api.post(`/splitpay/${loanId}/decline`);
            toast.success("Declined. The loan was cancelled.");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const repay = async (amount) => {
        setBusy(true);
        try {
            const body = amount === null ? {} : { amount };
            const r = await api.post(`/splitpay/${loanId}/repay`, body);
            toast.success(r.data.loan_fully_repaid ? "Group loan fully paid off!" : `Paid ${fmtCurrency(amount ?? myOutstanding)}`);
            setRepayOpen(false);
            setRepayAmount("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const acceptedCount = members.filter((m) => m.accepted).length;
    const progressPct = (acceptedCount / loan.member_count) * 100;
    const repaidCount = members.filter((m) => m.share_status === "repaid").length;

    return (
        <div data-testid="splitpay-detail-page">
            <MobileHeader title={isSupplierBulk ? "Bulk Credit Order" : "SplitPay™"} subtitle={loan.purpose || (isSupplierBulk ? "Bulk order" : "Group loan")} backTo={`${baseRoute}/splitpay`} variant="purple">
                <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-white/80">Total</div>
                            <div className="font-display text-2xl font-bold mt-0.5">{fmtCurrency(loan.amount)}</div>
                        </div>
                        <Badge className="bg-white/20 text-white border-white/30 capitalize">
                            <Icon size={11} className="mr-1" />{meta.label}
                        </Badge>
                    </div>
                    {loan.status === "pending_co_signers" && (
                        <div className="mt-3">
                            <div className="flex justify-between text-[10px] text-white/80 mb-1.5">
                                <span>{acceptedCount} of {loan.member_count} accepted</span>
                                <span>{Math.round(progressPct)}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                                <div className="h-full bg-white transition-all" style={{ width: `${progressPct}%` }} />
                            </div>
                        </div>
                    )}
                    {loan.status === "active" && (
                        <div className="mt-3">
                            <div className="flex justify-between text-[10px] text-white/80 mb-1.5">
                                <span>{repaidCount} of {loan.member_count} repaid</span>
                                <span>Due {fmtDate(loan.due_date)}</span>
                            </div>
                            <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                                <div className="h-full bg-white transition-all" style={{ width: `${(repaidCount / loan.member_count) * 100}%` }} />
                            </div>
                        </div>
                    )}
                </div>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-6 space-y-4">
                {/* Group Credit DNA score */}
                {loan.group_score && (
                    <GroupScoreCard
                        score={loan.group_score}
                        grade={loan.group_grade}
                        breakdown={loan.group_score_breakdown}
                        t={t}
                    />
                )}

                {/* Co-signer invite banner */}
                {canAccept && (
                    <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-5 text-white" data-testid="splitpay-invite-banner">
                        <div className="font-display text-lg font-bold flex items-center gap-2">
                            <AlertTriangle size={18} /> You're invited
                        </div>
                        <div className="text-sm text-white/90 mt-1">
                            {loan.initiator_name} invited you to co-sign this loan.
                            Your share is <span className="font-bold">{fmtCurrency(my_member.share_total)}</span> due in {loan.tenure_days} days.
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-4">
                            <Button onClick={decline} disabled={busy} variant="outline" className="h-11 rounded-xl border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent" data-testid="splitpay-decline-btn">
                                Decline
                            </Button>
                            <Button onClick={accept} disabled={busy} className="h-11 rounded-xl bg-white text-amber-700 hover:bg-white/90 font-semibold" data-testid="splitpay-accept-btn">
                                {busy ? "…" : "Accept invite"}
                            </Button>
                        </div>
                    </div>
                )}

                {/* My share card (or supplier-status card) */}
                {isSupplier ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="splitpay-supplier-status">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Supplier position</div>
                            <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] font-bold uppercase tracking-wider">Issuing credit</Badge>
                        </div>
                        <div className="font-display text-2xl font-bold text-[#0A1930] mt-2">{fmtCurrency(loan.amount)}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            You'll receive {fmtCurrency(loan.total_repayment)} total from buyers ({fmtCurrency(loan.interest + loan.fees)} earned).
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="splitpay-my-share">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">My share</div>
                            <ShareStatusBadge status={my_member.share_status} />
                        </div>
                        <div className="font-display text-3xl font-bold text-[#0A1930] mt-2">{fmtCurrency(my_member.share_total)}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {amIInitiator ? "Initiator" : (isSupplierBulk ? "Buyer" : "Co-signer")} · {fmtCurrency(my_member.share_repaid || 0)} paid · {fmtCurrency(myOutstanding)} outstanding
                        </div>
                        {loan.status === "active" && my_member.share_total > 0 && (
                            <Progress value={(my_member.share_repaid || 0) / my_member.share_total * 100} className="h-2 mt-3" />
                        )}
                        {canRepay && (
                            <Button
                                onClick={() => setRepayOpen(true)}
                                disabled={busy}
                                className="w-full mt-4 h-11 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6] font-semibold"
                                data-testid="splitpay-repay-btn"
                            >
                                Repay my share ({fmtCurrency(myOutstanding)})
                            </Button>
                        )}
                    </div>
                )}

                {/* Loan breakdown */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="font-display font-semibold text-[#0A1930] mb-3">Loan breakdown</div>
                    <div className="space-y-2 text-sm">
                        <Row label="Total amount" value={fmtCurrency(loan.amount)} />
                        <Row label="Tenure" value={`${loan.tenure_days} days`} />
                        <Row label="Total interest" value={fmtCurrency(loan.interest)} />
                        <Row label="Total fees" value={fmtCurrency(loan.fees)} />
                        <Row label="Total repayment" value={fmtCurrency(loan.total_repayment)} bold />
                        <Row label="Fund distribution" value={loan.fund_distribution === "initiator" ? "Initiator's wallet" : "Split equally"} />
                        {loan.interest_discount_applied > 0 && (
                            <div className="flex items-start gap-1.5 text-[11px] text-emerald-700 pt-2">
                                <Crown size={11} className="mt-0.5 flex-shrink-0" />
                                <span><span className="font-bold capitalize">{loan.tier_at_application} tier</span> applied −{(loan.interest_discount_applied * 100).toFixed(2).replace(/\.?0+$/, "")}% discount on interest</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Members */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-display font-semibold text-[#0A1930] flex items-center gap-2">
                            <Users size={16} className="text-[#7000FF]" /> Members ({members.length})
                        </div>
                    </div>
                    <div className="space-y-3">
                        {members.map((m) => <MemberCard key={m.id} m={m} />)}
                    </div>
                </div>
            </div>

            <RepayDialog
                open={repayOpen}
                onClose={() => setRepayOpen(false)}
                outstanding={myOutstanding}
                amount={repayAmount}
                setAmount={setRepayAmount}
                onSubmit={repay}
                busy={busy}
            />
        </div>
    );
}

function MemberCard({ m }) {
    const isOverdue = m.share_status === "overdue";
    const isRepaid = m.share_status === "repaid";
    const pendingAccept = !m.accepted && m.share_status === "pending_acceptance";
    return (
        <div className="flex items-center gap-3" data-testid={`member-row-${m.user_id}`}>
            <Avatar className="size-10 flex-shrink-0">
                <AvatarImage src={m.user_avatar} />
                <AvatarFallback className="bg-purple-100 text-purple-700 text-[10px] font-bold">
                    {m.user_name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?"}
                </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <div className="font-semibold text-sm text-[#0A1930] truncate">{m.user_name}</div>
                    {m.role === "initiator" && <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[9px]">Initiator</Badge>}
                </div>
                <div className="text-[11px] text-slate-500">
                    {fmtCurrency(m.share_repaid || 0)} / {fmtCurrency(m.share_total)} · {pendingAccept ? "Awaiting accept" : SHARE_STATUS_META[m.share_status]?.label}
                </div>
            </div>
            <div className="flex-shrink-0">
                {isRepaid && <CheckCircle2 size={20} className="text-emerald-500" />}
                {isOverdue && <AlertTriangle size={20} className="text-rose-500" />}
                {pendingAccept && <Clock size={20} className="text-amber-500" />}
            </div>
        </div>
    );
}

function ShareStatusBadge({ status }) {
    const meta = SHARE_STATUS_META[status] || SHARE_STATUS_META.active;
    const colorMap = {
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        blue:  "bg-blue-50 text-blue-700 border-blue-200",
        green: "bg-emerald-50 text-emerald-700 border-emerald-200",
        rose:  "bg-rose-50 text-rose-700 border-rose-200",
        slate: "bg-slate-50 text-slate-600 border-slate-200",
    };
    return (
        <Badge className={`${colorMap[meta.color]} text-[10px] font-bold uppercase tracking-wider`}>
            {meta.label}
        </Badge>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex justify-between">
            <span className="text-slate-500">{label}</span>
            <span className={bold ? "font-display text-base font-bold text-[#0A1930]" : "font-semibold text-[#0A1930]"}>
                {value}
            </span>
        </div>
    );
}

function RepayDialog({ open, onClose, outstanding, amount, setAmount, onSubmit, busy }) {
    const parsed = parseFloat(amount) || 0;
    const willPay = amount ? Math.min(parsed, outstanding) : outstanding;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-sm" data-testid="splitpay-repay-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <WalletIcon size={18} className="text-[#7000FF]" /> Repay my share
                    </DialogTitle>
                    <DialogDescription>
                        Pay your full share or any amount up to the outstanding balance.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="bg-slate-50 rounded-lg p-3 text-sm">
                        <div className="flex justify-between">
                            <span className="text-slate-500">Outstanding</span>
                            <span className="font-bold text-[#0A1930]">{fmtCurrency(outstanding)}</span>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Amount (leave blank to pay in full)</Label>
                        <Input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder={`${outstanding.toFixed(2)}`}
                            className="mt-1 h-11 rounded-xl"
                            data-testid="splitpay-repay-amount"
                        />
                    </div>
                    <div className="text-xs text-slate-500">
                        Paying <span className="font-bold text-[#0A1930]">{fmtCurrency(willPay)}</span> from your wallet.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={() => onSubmit(amount ? parsed : null)}
                        disabled={busy || (amount && parsed <= 0)}
                        className="bg-[#7000FF] hover:bg-[#5d00d6]"
                        data-testid="splitpay-repay-confirm"
                    >
                        {busy ? "…" : "Confirm"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
